import React, { useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  VStack,
  Collapse,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { getCategoryBadgeCount } from './cartUtils';
import WeightServiceRow from './WeightServiceRow';
import PieceServiceRow from './PieceServiceRow';
import { formatOverageRate, weightCap } from './bagSizeRules';

/**
 * CategoryAccordion — Controlled accordion with single-expanded behavior.
 *
 * Renders one collapsible section per category group. Only one section
 * can be expanded at a time (controlled by parent). Collapsed sections
 * with items in cart show a badge count.
 */
export default function CategoryAccordion({
  groups,
  expandedCategoryId,
  onToggle,
  cart,
  dispatch,
  themeColor,
  bagSizeDisclaimer = '',
  bagSizeGuide = [],
  bagOverageRate = 1.95,
}) {
  const colorScheme = themeColor || 'blue';
  const cartItems = cart?.items || [];
  const disclaimer = (bagSizeDisclaimer || '').trim();
  const overageLabel = formatOverageRate(bagOverageRate);
  const guideRows = (bagSizeGuide || []).filter((row) => (row.label || '').trim());

  // Refs to each section wrapper so we can bring the just-expanded category's
  // header into view. Without this, expanding a category lower in the list
  // collapses the section above it (shrinking content) and the browser's scroll
  // anchoring leaves the viewport parked below — on the add-ons/footer — so the
  // user has to scroll back up to see the items they just opened.
  const sectionRefs = useRef({});
  const didMountRef = useRef(false);

  useEffect(() => {
    // Skip the initial mount (first category is expanded by default and already
    // at the top) — only scroll when the user opens a different category.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!expandedCategoryId) return;
    const el = sectionRefs.current[expandedCategoryId];
    if (!el) return;
    // Wait for the collapse/expand animation + layout to settle, then bring the
    // opened section's header to the top of the viewport.
    const t = setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {
        el.scrollIntoView();
      }
    }, 120);
    return () => clearTimeout(t);
  }, [expandedCategoryId]);

  return (
    <VStack spacing={3} align="stretch" w="100%">
      {groups.map((group) => {
        const isExpanded = expandedCategoryId === group.categoryId;
        const badgeCount = getCategoryBadgeCount(cartItems, group.categoryId);
        // The bag size disclaimer shows ONLY in the actual per-bag section
        // (category name contains "bag"), not every non-weight category — so
        // flat-rate sections like "Comforters, Sarees" don't show it.
        const isBagCategory = (group.services || []).length > 0 &&
          (group.services || []).every(
            (s) => !(s.inputWeight === true || s.inputWeight === 'true')
          ) && (
            /bag/i.test(group.categoryName || '')
            || (group.services || []).some((s) => /bag|gallon/i.test(s.serviceName || ''))
          );

        return (
          <Box
            key={group.categoryId}
            ref={(el) => { sectionRefs.current[group.categoryId] = el; }}
            borderRadius="xl"
            border="1px solid"
            borderColor={isExpanded ? `${colorScheme}.200` : 'gray.200'}
            overflow="hidden"
            bg="white"
            scrollMarginTop="12px"
          >
            {/* Accordion Header */}
            <Flex
              as="button"
              w="100%"
              p={4}
              align="center"
              justify="space-between"
              onClick={() => onToggle(group.categoryId)}
              bg={isExpanded ? `${colorScheme}.50` : 'white'}
              _hover={{ bg: isExpanded ? `${colorScheme}.50` : 'gray.50' }}
              transition="background 0.2s"
              cursor="pointer"
              border="none"
              textAlign="left"
            >
              <Flex align="center" gap={3}>
                <Text fontWeight="700" fontSize="md" color="gray.800">
                  {group.categoryName}
                </Text>
                {!isExpanded && badgeCount > 0 && (
                  <Badge
                    colorScheme={colorScheme}
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="xs"
                  >
                    {badgeCount} item{badgeCount !== 1 ? 's' : ''} added
                  </Badge>
                )}
              </Flex>
              {isExpanded ? (
                <ChevronUpIcon boxSize={5} color="gray.500" />
              ) : (
                <ChevronDownIcon boxSize={5} color="gray.500" />
              )}
            </Flex>

            {/* Accordion Content */}
            <Collapse in={isExpanded} animateOpacity>
              <VStack spacing={2} p={3} align="stretch">
                {isBagCategory && guideRows.length > 0 && (
                  <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                    {guideRows.map((row) => {
                      const cap = weightCap(row);
                      return (
                        <Flex key={row.label} px={3} py={2} justify="space-between" gap={2}
                              borderBottom="1px solid" borderColor="gray.100" fontSize="xs">
                          <Box>
                            <Text fontWeight="700" color="gray.800">{row.label}</Text>
                            {row.capacity ? <Text color="gray.500">{row.capacity}</Text> : null}
                          </Box>
                          <Box textAlign="right">
                            {cap > 0 ? <Text fontWeight="600">Includes {cap} lb</Text> : null}
                            {overageLabel && cap > 0 ? <Text color="gray.500">Extra {overageLabel}</Text> : null}
                            {row.price ? <Text color="gray.600">{String(row.price).startsWith('$') ? row.price : `$${row.price}`}</Text> : null}
                          </Box>
                        </Flex>
                      );
                    })}
                  </Box>
                )}
                {group.services.map((service) => {
                  const svcId = service.serviceId || service.serviceName;
                  const cartItem = cartItems.find(
                    (item) => item.serviceId === svcId
                  );
                  const isWeight =
                    service.inputWeight === true ||
                    service.inputWeight === 'true';
                  const serviceWithId = { ...service, serviceId: svcId };

                  if (isWeight) {
                    return (
                      <WeightServiceRow
                        key={svcId}
                        service={serviceWithId}
                        cartItem={cartItem}
                        dispatch={dispatch}
                      />
                    );
                  }
                  return (
                    <PieceServiceRow
                      key={svcId}
                      service={serviceWithId}
                      cartItem={cartItem}
                      dispatch={dispatch}
                      bagSizeGuide={bagSizeGuide}
                      bagOverageRate={bagOverageRate}
                    />
                  );
                })}
                {/* Bag size disclaimer — shown inside the per-bag section only. */}
                {isBagCategory && disclaimer && (
                  <Box bg={`${colorScheme}.50`} border="1px solid" borderColor={`${colorScheme}.100`} borderRadius="lg" px={3} py={2}>
                    <Text fontSize="xs" color="gray.600" lineHeight="short">{disclaimer}</Text>
                  </Box>
                )}
              </VStack>
            </Collapse>
          </Box>
        );
      })}
    </VStack>
  );
}
